import React, { useState, useEffect, useRef } from "react";
import { FiSearch, FiEdit2, FiTrash2 } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { MdKeyboardArrowLeft } from "react-icons/md";
import axiosConfig from "../../utils/axiosInstance";
import { updateAddress } from "../../utils/accountApi";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setAddress } from "../../redux/addressSlice";
import { Modal } from "react-bootstrap";
import "../Pages/address.css";
import "../Pages/addresses.css";
import "./AddressModal.css";

const API_KEY = "AIzaSyDD8frd15FoMhemosVqGvVBCHaRjLgNszc";

export default function AddressModal({ show, onHide, addressType = "both", onSuccess }) {
    const userId = localStorage.getItem("userid");
    const [step, setStep] = useState("default");
    const [editId, setEditId] = useState(null);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zipcodeCheck, setZipcodeCheck] = useState(
        () => localStorage.getItem("verifiedZipcode") || ""
    );
    const [zipcodeStatus, setZipcodeStatus] = useState(
        () => localStorage.getItem("verifiedZipcode") ? { status: "Available" } : null
    );
    const [checkingZip, setCheckingZip] = useState(false);
    const [geoLocating, setGeoLocating] = useState(false);
    const [geoError, setGeoError] = useState("");
    const [formErrors, setFormErrors] = useState({});
    const inputRef = useRef();
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const dispatch = useDispatch();

    const emptyForm = {
        name: "",
        address_line_1: "",
        address_line_2: "",
        flat_no: "",
        landmark: "",
        zipcode: "",
        latitude: "",
        longitude: "",
        mobileno: "",
        country: "USA",
        state: "",
        city: "",
        user: userId
    };

    const [formData, setFormData] = useState({ ...emptyForm });

    useEffect(() => {
        if (!show) return;
        if (window.google) return setScriptLoaded(true);
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => setScriptLoaded(true);
        document.head.appendChild(script);
    }, [show]);

    async function fetchAddress() {
        if (!userId) return;
        try {
            const res = await axiosConfig.get(`/accounts/address/?user=${userId}&is_suspended=false`);
            const list = res?.data?.results ?? [];
            setAddresses(list);
            dispatch(setAddress(list));

            const isNewCreated = localStorage.getItem("newlyCreatedModalAddr") === "true";
            if (isNewCreated && list.length > 0) {
                const newest = [...list].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
                if (newest) {
                    const idx = list.findIndex(a => a.id === newest.id);
                    setSelectedIndex(idx >= 0 ? idx : 0);
                    localStorage.setItem("defaultAddressId", String(newest.id));
                    localStorage.setItem("defaultAddress", JSON.stringify(newest));
                    if (addressType === "rental") localStorage.setItem("rentalAddress", JSON.stringify(newest));
                    else localStorage.setItem("saleAddress", JSON.stringify(newest));
                }
                localStorage.removeItem("newlyCreatedModalAddr");
            } else {
                const savedId = localStorage.getItem("defaultAddressId");
                if (savedId) {
                    const index = list.findIndex(a => String(a?.id) === String(savedId));
                    if (index !== -1) {
                        setSelectedIndex(index);
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    async function fetchUserProfile() {
        if (!userId) return;
        try {
            const res = await axiosConfig.get(`/user_data/users/${userId}/`);
            setUserProfile(res.data?.results || res.data);
        } catch (error) {
            console.error("Failed to fetch user profile", error);
        }
    }

    useEffect(() => {
        if (show) {
            fetchAddress();
            fetchUserProfile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, show]);

    useEffect(() => {
        setZipcodeCheck("");
        setZipcodeStatus(null);
    }, [addressType]);

    useEffect(() => {
        if (show) {
            setStep("default");
            setQuery("");
            setSuggestions([]);
            setSelected(null);
            setZipcodeCheck(localStorage.getItem("verifiedZipcode") || "");
            setZipcodeStatus(localStorage.getItem("verifiedZipcode") ? { status: "Available" } : null);
            setEditId(null);
            setFormData({ ...emptyForm });
            setFormErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const checkZipcode = async () => {
        if (!zipcodeCheck) return;

        setCheckingZip(true);
        try {
            let url = `/masters/check-zipcode-availability/?`;
            if (addressType === "rental") {
                url += `rental_zipcode=${zipcodeCheck}`;
            } else {
                url += `sale_zipcode=${zipcodeCheck}`;
            }

            const res = await axiosConfig.get(url);
            const statusData =
                addressType === "rental"
                    ? res?.data?.rental_zipcode ?? null
                    : res?.data?.sale_zipcode ?? null;

            setZipcodeStatus(statusData);
            if (statusData?.status === "Available") {
                setFormData((prev) => ({ ...prev, zipcode: zipcodeCheck }));
                localStorage.setItem("verifiedZipcode", zipcodeCheck);
            } else {
                localStorage.removeItem("verifiedZipcode");
            }
        } catch (error) {
            setZipcodeStatus({ status: "Error", message: "Something went wrong" });
        } finally {
            setCheckingZip(false);
        }
    };

    const processGeocodeResults = (res, lat, lng, fallbackAddress = "") => {
        if (!res || res.length === 0) {
            if (fallbackAddress) {
                setSelected({ lat: Number(lat), lng: Number(lng), address: fallbackAddress, components: [] });
                setQuery(fallbackAddress);
            }
            return;
        }

        const bestWithZip = res.find(r => r.address_components?.some(c => c.types.includes("postal_code"))) || res[0];
        const addr = bestWithZip.formatted_address || res[0].formatted_address || fallbackAddress;

        const components = [...(bestWithZip.address_components || res[0].address_components || [])];
        const requiredTypes = ["postal_code", "locality", "administrative_area_level_1", "country"];

        requiredTypes.forEach(reqType => {
            const hasType = components.some(c => c.types?.includes(reqType));
            if (!hasType) {
                for (const r of res) {
                    const found = r.address_components?.find(c => c.types?.includes(reqType));
                    if (found) {
                        components.push(found);
                        break;
                    }
                }
            }
        });

        setSelected({ lat: Number(lat), lng: Number(lng), address: addr, components });
        setQuery(addr);
    };

    const selectAustinFallback = (msg = "") => {
        const lat = 30.2672;
        const lng = -97.7431;
        if (msg) setGeoError(msg);
        const waitForGoogle = (attempts = 0) => {
            if (window.google?.maps) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (res, status) => {
                    setGeoLocating(false);
                    if (status === "OK" && res && res.length > 0) {
                        processGeocodeResults(res, lat, lng, "Austin, TX, USA");
                    } else {
                        setSelected({ lat, lng, address: "Austin, TX, USA", components: [] });
                        setQuery("Austin, TX, USA");
                    }
                });
            } else if (attempts < 20) {
                setTimeout(() => waitForGoogle(attempts + 1), 300);
            } else {
                setGeoLocating(false);
                setSelected({ lat, lng, address: "Austin, TX, USA", components: [] });
                setQuery("Austin, TX, USA");
            }
        };
        waitForGoogle();
    };

    const detectCurrentLocation = () => {
        if (!navigator.geolocation) {
            selectAustinFallback("Geolocation is not supported by your browser. Showing Austin, USA location.");
            return;
        }
        setGeoLocating(true);
        setGeoError("");
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const waitForGoogle = (attempts = 0) => {
                    if (window.google?.maps) {
                        const geocoder = new window.google.maps.Geocoder();
                        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
                            setGeoLocating(false);
                            if (status === "OK" && res && res.length > 0) {
                                processGeocodeResults(res, latitude, longitude);
                            } else {
                                selectAustinFallback("Could not determine current address. Showing Austin, USA location.");
                            }
                        });
                    } else if (attempts < 20) {
                        setTimeout(() => waitForGoogle(attempts + 1), 300);
                    } else {
                        selectAustinFallback("Maps not ready. Showing Austin, USA location.");
                    }
                };
                waitForGoogle();
            },
            (err) => {
                if (err.code === 1) {
                    selectAustinFallback("Location permission denied. Showing Austin, USA location.");
                } else {
                    selectAustinFallback("Unable to fetch location. Showing Austin, USA location.");
                }
            },
            { timeout: 10000, maximumAge: 60000 }
        );
    };

    const handleAddAddress = () => {
        if (!zipcodeCheck || zipcodeStatus?.status !== "Available") {
            toast.error("Please enter and verify a valid zipcode before adding a new address");
            return;
        }
        // Do NOT pre-fill zipcode here — it will be set from the geocoded selected location.
        // This prevents the initial verified zipcode from bleeding into the form when
        // the user picks a different location on the map or via search.
        setFormData({ 
            ...emptyForm, 
            name: userProfile?.full_name || "", 
            mobileno: userProfile?.mobileno || "" 
        });
        setEditId(null);
        setGeoError("");
        setQuery("");
        setSuggestions([]);
        setSelected(null);
        setStep("add");
        detectCurrentLocation();
    };

    const startEdit = (address) => {
        setEditId(address.id);
        setFormData({
            name: address.name || "",
            address_line_1: address.address_line_1 || "",
            address_line_2: address.address_line_2 || "",
            flat_no: address.flat_no || "",
            landmark: address.landmark || "",
            zipcode: address.zipcode || "",
            mobileno: address.mobileno || "",
            country: address.country || "USA",
            state: address.state || "",
            city: address.city || "",
            latitude: address.latitude || "",
            longitude: address.longitude || "",
            user: userId
        });
        setSelected({
            lat: address.latitude,
            lng: address.longitude,
            address: address.address_line_1,
        });
        setQuery(address.address_line_1 || "");
        setStep("address");
    };

    const deleteAddressItem = async (id) => {
        try {
            await axiosConfig.delete(`/accounts/address/${id}/`);
            toast.success("Address deleted successfully");
            fetchAddress();
        } catch (error) {
            toast.error("Failed to delete address");
        }
    };

    const resetMapRefs = () => {
        if (markerRef.current) markerRef.current.setMap(null);
        mapRef.current = null;
        markerRef.current = null;
    };

    const placeMarker = (position) => {
        if (!mapRef.current || !window.google) return;
        if (markerRef.current) {
            markerRef.current.setPosition(position);
            mapRef.current.panTo(position);
            mapRef.current.setZoom(16);
        } else {
            markerRef.current = new window.google.maps.Marker({
                position,
                map: mapRef.current,
                animation: window.google.maps.Animation.DROP,
            });
            mapRef.current.panTo(position);
            mapRef.current.setZoom(16);
        }
    };

    const initMap = (el) => {
        if (!el || mapRef.current) return;
        const waitForGoogle = (attempts = 0) => {
            if (window.google?.maps) {
                const defaultCenter = { lat: 30.2672, lng: -97.7431 }; // Austin, TX
                mapRef.current = new window.google.maps.Map(el, {
                    center: defaultCenter,
                    zoom: 5,
                    disableDefaultUI: false,
                });
                mapRef.current.addListener("click", (e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (res, status) => {
                        if (status === "OK" && res && res.length > 0) {
                            processGeocodeResults(res, lat, lng);
                        }
                    });
                });
                if (selected) {
                    const pos = { lat: Number(selected.lat), lng: Number(selected.lng) };
                    placeMarker(pos);
                }
            } else if (attempts < 30) {
                setTimeout(() => waitForGoogle(attempts + 1), 200);
            }
        };
        waitForGoogle();
    };

    useEffect(() => {
        if (!selected || !mapRef.current || !window.google) return;
        const pos = { lat: Number(selected.lat), lng: Number(selected.lng) };
        placeMarker(pos);
    }, [selected]);

    useEffect(() => {
        if (step !== "add") {
            resetMapRefs();
        }
    }, [step]);

    const getSuggestions = (val) => {
        if (!window.google || val.length < 2) return setSuggestions([]);
        new window.google.maps.places.AutocompleteService().getPlacePredictions(
            { input: val },
            (preds, status) =>
                status === "OK" ? setSuggestions(preds) : setSuggestions([])
        );
    };

    const handleSelect = (place) => {
        setQuery(place.description);
        setSuggestions([]);
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: place.place_id }, (res, status) => {
            if (status === "OK" && res && res[0]) {
                const loc = res[0].geometry.location;
                const lat = loc.lat();
                const lng = loc.lng();
                geocoder.geocode({ location: { lat, lng } }, (revRes, revStatus) => {
                    if (revStatus === "OK" && revRes && revRes.length > 0) {
                        processGeocodeResults(revRes, lat, lng, place.description);
                    } else {
                        processGeocodeResults(res, lat, lng, place.description);
                    }
                });
            }
        });
    };

    useEffect(() => {
        if (selected?.address && selected?.components) {
            let city = "", state = "", country = "", zipcode = "";
            let localParts = [];
            if (selected.components) {
                selected.components.forEach((comp) => {
                    if (comp.types.includes("locality")) city = comp.long_name;
                    if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
                    if (comp.types.includes("country")) country = comp.long_name;
                    if (comp.types.includes("postal_code")) zipcode = comp.long_name;

                    const ignoreTypes = ["locality", "administrative_area_level_1", "administrative_area_level_2", "country", "postal_code", "postal_code_suffix"];
                    if (!comp.types.some(t => ignoreTypes.includes(t))) {
                        localParts.push(comp.long_name);
                    }
                });
            }

            const line1 = localParts.length > 0 ? localParts.join(", ") : selected.address;

            // Always unconditionally overwrite location-derived fields so that
            // switching from one location to another never retains stale values
            // (e.g. the initial current-location zipcode vs. the newly selected one).
            setFormData(prev => ({
                ...prev,
                address_line_1: line1,
                city,          // empty string if geocoding didn't return one
                state,         // empty string if geocoding didn't return one
                country: country || prev.country, // keep default "India" if not returned
                zipcode,       // empty string if geocoding didn't return one
                latitude: selected.lat || prev.latitude,
                longitude: selected.lng || prev.longitude,
            }));
        }
    }, [selected]);

    async function formSubmit(e) {
        e.preventDefault();
        setFormErrors({});

        try {
            const result = await validateZipcode(formData.zipcode);
            const isAvailable =
                addressType === "sale"
                    ? result.sale_zipcode?.status === "Available"
                    : addressType === "rental"
                        ? result.rental_zipcode?.status === "Available"
                        : result.sale_zipcode?.status === "Available" || result.rental_zipcode?.status === "Available";

            if (!isAvailable) {
                toast.error("The selected zipcode is outside our service area.");
                return;
            }
        } catch (error) {
            toast.error("Could not verify the zipcode. Please try again.");
            return;
        }

        const payload = {
            ...formData,
            latitude: selected?.lat || formData.latitude || "",
            longitude: selected?.lng || formData.longitude || "",
            user: userId
        };

        try {
            if (editId) {
                await updateAddress(editId, payload);
                toast.success("Address updated successfully");
            } else {
                const res = await axiosConfig.post("/accounts/address/", payload, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
                if (res.data.success || res.data) {
                    toast.success(res.data.message || "Address added successfully");
                }
                localStorage.setItem("newlyCreatedModalAddr", "true");
            }

            if (payload.zipcode) {
                localStorage.setItem("verifiedZipcode", payload.zipcode);
                localStorage.setItem("savedZipcode", payload.zipcode);
                setZipcodeCheck(payload.zipcode);
                setZipcodeStatus({ status: "Available" });
            } else {
                setZipcodeCheck("");
                setZipcodeStatus(null);
            }

            setQuery("");
            setSuggestions([]);
            setSelected(null);
            setEditId(null);
            setStep("default");
            await fetchAddress();
        } catch (error) {
            console.log(error);
            if (error.response?.data) {
                setFormErrors(error.response.data);
                toast.error("Please fix the errors in the form");
            } else {
                toast.error("An error occurred while saving the address");
            }
        }
    }

    async function validateZipcode(zipcode) {
        try {
            let url = `/masters/check-zipcode-availability/?`;

            if (addressType === 'sale') {
                url += `sale_zipcode=${zipcode}`;
            } else if (addressType === 'rental') {
                url += `rental_zipcode=${zipcode}`;
            } else {
                url += `sale_zipcode=${zipcode}`; // Both mode fallback
            }

            const response = await axiosConfig.get(url);
            return response.data;
        } catch (error) {
            console.error("Zipcode validation error:", error);
            throw error;
        }
    }

    async function handleValidate(address, index) {
        if (!address) {
            toast.error(`Please select ${addressType} delivery address`);
            return;
        }

        if (!address.zipcode) {
            toast.error("Selected address doesn't have a zipcode");
            return;
        }

        try {
            const result = await validateZipcode(address.zipcode);

            const isAvailable =
                addressType === "sale"
                    ? result.sale_zipcode?.status === "Available"
                    : addressType === "rental"
                        ? result.rental_zipcode?.status === "Available"
                        : result.sale_zipcode?.status === "Available" && result.rental_zipcode?.status !== "Unavailable";

            if (isAvailable) {
                setSelectedIndex(index);
                localStorage.setItem("defaultAddressId", address?.id);

                if (addressType === 'sale') {
                    localStorage.setItem('saleAddress', JSON.stringify(address));
                    toast.success("Sale delivery available for this zipcode!");
                } else if (addressType === 'rental') {
                    localStorage.setItem('rentalAddress', JSON.stringify(address));
                    toast.success("Rental delivery available for this zipcode!");
                } else {
                    localStorage.setItem('saleAddress', JSON.stringify(address));
                    localStorage.setItem('rentalAddress', JSON.stringify(address));
                    toast.success("Delivery available for this zipcode!");
                }

                if (onSuccess) {
                    onSuccess(address);
                } else {
                    onHide();
                }
            } else {
                toast.error(`Delivery not available for zipcode: ${address.zipcode}`);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Validation failed");
        }
    }

    return (
        <Modal show={show} onHide={onHide} dialogClassName="address-modal-xl" className="template-construction address-select-modal">
            <Modal.Header style={{ borderBottom: "1px solid #eee", padding: "15px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button type="button" className="address-modal-back-btn" onClick={onHide}>
                        <MdKeyboardArrowLeft size={24} />
                    </button>
                    <Modal.Title style={{ fontSize: "20px", fontWeight: "600", margin: 0, color: "#333" }}>Select Address</Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body style={{ padding: "30px 40px 40px" }}>
                <div style={{ width: "100%", paddingBottom: "40px" }}>

                    {step === "default" && (
                        <>
                            <div className="am-top-section">
                                <div style={{
                                    border: "1px solid #E8E9EC",
                                    borderRadius: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    background: "#fafbfc",
                                    justifyContent: "center",
                                    padding: "20px 24px"
                                }}>
                                    <div className="am-check-box" style={{ flexDirection: "column", alignItems: "flex-start", gap: "10px" }}>
                                        <div className="am-check-title">Check Service Available In Your Area</div>
                                        <div className="am-check-input-group">
                                            <input
                                                type="text"
                                                className="am-check-input"
                                                placeholder="Enter Pincode"
                                                maxLength={5}
                                                value={zipcodeCheck}
                                                onChange={(e) => {
                                                    setZipcodeCheck(e.target.value.replace(/\D/g, ''));
                                                    setZipcodeStatus(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        if (!checkingZip && zipcodeCheck.length === 5) {
                                                            checkZipcode();
                                                        }
                                                    }
                                                }}
                                            />
                                            <button
                                                className={`am-check-btn
  ${zipcodeCheck.length === 5 ? "am-check-btn-active" : ""}
  ${zipcodeStatus?.status === "Available" ? "am-check-btn-success" : ""}
`}

                                                onClick={checkZipcode}
                                                disabled={checkingZip || zipcodeCheck.length < 5}
                                            >

                                                {checkingZip ? "..." : "Check"}
                                            </button>
                                        </div>
                                        {zipcodeStatus && (
                                            <div style={{ display: "flex", alignItems: "center", fontSize: "14px", fontWeight: "500", color: zipcodeStatus?.status === "Available" ? "#28a745" : "#dc3545" }}>
                                                {zipcodeStatus?.status === "Available" ? "✓ Available" : "✗ Not available"}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button className="am-add-btn" onClick={handleAddAddress}>
                                    +Add New Address
                                </button>
                            </div>

                            {addresses.length > 0 && (
                                <>
                                    <div className="am-saved-title">SAVED ADDRESSES</div>
                                    <div className="am-cards-list">
                                        {addresses.map((add, ind) => {
                                            const isSelected = selectedIndex === ind;
                                            return (
                                                <div
                                                    key={add.id}
                                                    className={`am-card ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleValidate(add, ind)}
                                                >
                                                    <div className="am-card-header">
                                                        <div className="am-card-name">
                                                            Delivering to: <span>{add.name}</span>
                                                        </div>
                                                        <div className="am-card-radio"></div>
                                                    </div>
                                                    <div className="am-card-address">
                                                        Floor : {[add.flat_no, add.address_line_1, add.address_line_2, add.city, add.state, add.country].filter(Boolean).join(", ")}
                                                    </div>
                                                    <div className="am-card-actions" onClick={(e) => e.stopPropagation()}>
                                                        <button className="am-action-btn edit" onClick={() => startEdit(add)}>
                                                            <FiEdit2 size={14} /> Edit
                                                        </button>
                                                        <button className="am-action-btn remove" onClick={() => deleteAddressItem(add.id)}>
                                                            <FiTrash2 size={14} /> Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {step === "add" && (
                        <>
                            <h3 className="address-add-new" style={{ marginBottom: "20px" }}>
                                <button
                                    onClick={() => setStep("default")}
                                    style={{ background: "none", border: "none", color: "#666", marginRight: "10px", padding: 0 }}
                                >
                                    <MdKeyboardArrowLeft size={24} />
                                </button>
                                Add new address
                            </h3>

                            {geoLocating && (
                                <div style={{ color: "#ff8c00", marginBottom: "15px", fontWeight: "500" }}>
                                    Detecting your current location...
                                </div>
                            )}

                            <div className="address-search-bar" style={{ width: "100%" }}>
                                <input
                                    ref={inputRef}
                                    value={query}
                                    disabled={geoLocating}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        getSuggestions(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (suggestions.length > 0) {
                                                handleSelect(suggestions[0]);
                                            } else if (query.trim() && window.google?.maps) {
                                                const geocoder = new window.google.maps.Geocoder();
                                                geocoder.geocode({ address: query.trim() }, (res, status) => {
                                                    if (status === "OK" && res && res[0]) {
                                                        const loc = res[0].geometry.location;
                                                        const lat = loc.lat();
                                                        const lng = loc.lng();
                                                        geocoder.geocode({ location: { lat, lng } }, (revRes, revStatus) => {
                                                            if (revStatus === "OK" && revRes && revRes.length > 0) {
                                                                processGeocodeResults(revRes, lat, lng, query.trim());
                                                            } else {
                                                                processGeocodeResults(res, lat, lng, query.trim());
                                                            }
                                                        });
                                                    } else {
                                                        toast.error("Location not found. Please try moving the pin on the map or selecting from suggestions.");
                                                    }
                                                });
                                            }
                                        }
                                    }}
                                    placeholder="Search my location"
                                    style={{ width: "100%" }}
                                />
                                <FiSearch className="address-search-icon" style={{ marginTop: "6px" }} />
                                {suggestions.length > 0 && (
                                    <div className="suggestions-dropdown" style={{ width: "100%" }}>
                                        {suggestions.map((s) => (
                                            <div key={s.place_id} onClick={() => handleSelect(s)} className="map-suggestion">
                                                <IoLocationOutline className="map-alt" />  <span>{s.description}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "15px" }}>
                                <button
                                    type="button"
                                    className="am-check-btn"
                                    style={{ display: "flex", alignItems: "center", background: "#fdfdfd", border: "1px solid #ddd" }}
                                    onClick={detectCurrentLocation}
                                    disabled={geoLocating}
                                >
                                    <IoLocationOutline style={{ marginRight: 8, color: "#ff8c00", fontSize: "18px" }} />
                                    {geoLocating ? "Detecting..." : "Use Current Location"}
                                </button>
                            </div>

                            {geoError && <div style={{ color: "#dc3545", marginTop: "10px", fontSize: "14px", textAlign: "right" }}>{geoError}</div>}

                            {!scriptLoaded && <p>Loading maps...</p>}

                            <div ref={initMap} className="map-view" style={{ minHeight: "350px", width: "100%", borderRadius: "8px", marginTop: "15px", border: "1px solid #ddd" }} />

                            {selected && (
                                <div style={{ marginTop: "20px" }}>
                                    <div className="selected-address" style={{ marginBottom: "15px" }}>
                                        <div className="selected-address">
                                            <IoLocationOutline className="map-alt" /> {selected.address}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <button
                                            type="button"
                                            className="am-add-btn"
                                            onClick={() => {
                                                setSuggestions([]);
                                                setStep("address");
                                            }}
                                        >
                                            Confirm Location
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {step === "address" && (
                        <form className="address-form am-form-container" onSubmit={formSubmit}>
                            <h3 className="address-add-new" style={{ marginBottom: "20px" }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (editId) {
                                            setStep("default");
                                            setEditId(null);
                                            setFormData({ ...emptyForm });
                                        } else {
                                            setStep("add");
                                        }
                                    }}
                                    style={{ background: "none", border: "none", color: "#666", marginRight: "10px", padding: 0 }}
                                >
                                    <MdKeyboardArrowLeft size={24} />
                                </button>
                                {editId ? "Edit Address Details" : "Enter Address Details"}
                            </h3>
                            <div className="selected-address-box" style={{ width: "100%", marginBottom: "20px" }}>
                                <div className="address-head">
                                    <div className="area-name"><IoLocationOutline className="map-alt me-1" />{selected?.address?.split(",")[0] || formData.address_line_1}</div>
                                    <button
                                        type="button"
                                        className="am-check-btn"
                                        style={{ padding: "5px 10px", fontSize: "12px" }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setQuery("");
                                            setSuggestions([]);
                                            setStep("add");
                                        }}
                                    >
                                        CHANGE
                                    </button>
                                </div>
                                <div className="area-details" style={{ maxWidth: "100%" }}>{selected?.address || formData.address_line_1}</div>
                            </div>
                            <div className="address-form-box" style={{ width: "100%" }}>
                                <div className="input-field">
                                    <label>Country  <span className="sup-color">*</span></label>
                                    <input
                                        type="text"
                                        className="input-box-address"
                                        placeholder="Country"
                                        value={formData.country}
                                        required
                                        onChange={(e) =>
                                            setFormData({ ...formData, country: e.target.value })
                                        }
                                    />
                                    {formErrors.country && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.country[0]}</div>}
                                </div>
                                <div className="input-double">
                                    <div className="input-field">
                                        <label>Full Name  <span className="sup-color">*</span></label>
                                        <input
                                            type="text"
                                            className="input-box-address"
                                            placeholder="Full Name"
                                            value={formData.name}
                                            required
                                            onChange={(e) =>
                                                setFormData({ ...formData, name: e.target.value })
                                            }
                                        />
                                        {formErrors.name && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.name[0]}</div>}
                                    </div>
                                    <div className="input-field">
                                        <label>Mobile Number <span className="sup-color">*</span></label>
                                        <input
                                            type="text"
                                            className="input-box-address"
                                            placeholder="Mobile Number"
                                            value={formData.mobileno}
                                            required
                                            maxLength={10}
                                            onChange={(e) =>
                                                setFormData({ ...formData, mobileno: e.target.value.replace(/\D/g, "") })
                                            }
                                        />
                                        {formErrors.mobileno && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.mobileno[0]}</div>}
                                    </div>
                                </div>
                                <div className="label-title">
                                    <div className="input-double">
                                        <div className="input-field">
                                            <label>Flat no  </label>
                                            <input
                                                type="text"
                                                className="input-box-address"
                                                placeholder="House/Apartment no"
                                                value={formData.flat_no}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, flat_no: e.target.value })
                                                }
                                            />
                                            {formErrors.flat_no && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.flat_no[0]}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="input-field">
                                    <label>Address line 1  <span className="sup-color">*</span></label>
                                    <input
                                        type="text"
                                        className="input-box-address"
                                        placeholder="Address line 1"
                                        value={formData.address_line_1}
                                        required
                                        onChange={(e) =>
                                            setFormData({ ...formData, address_line_1: e.target.value })
                                        }
                                    />
                                    {formErrors.address_line_1 && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.address_line_1[0]}</div>}
                                </div>
                                <div className="input-field">
                                    <label>Address line 2  <span className="sup-color">*</span></label>
                                    <input
                                        type="text"
                                        className="input-box-address"
                                        placeholder="Address line 2"
                                        value={formData.address_line_2}
                                        onChange={(e) =>
                                            setFormData({ ...formData, address_line_2: e.target.value })
                                        }
                                    />
                                    {formErrors.address_line_2 && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.address_line_2[0]}</div>}
                                </div>
                                <div className="label-title">
                                    <div className="input-double">
                                        <div className="input-field">
                                            <label>Zipcode  <span className="sup-color">*</span></label>
                                            <input
                                                type="text"
                                                className="input-box-address"
                                                placeholder="Zipcode"
                                                value={formData.zipcode}
                                                maxLength={5}
                                                required
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                                                    setFormData({ ...formData, zipcode: onlyNumbers });
                                                }}
                                            />
                                            {formErrors.zipcode && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.zipcode[0]}</div>}
                                        </div>

                                        <div className="input-field">
                                            <label>State  <span className="sup-color">*</span></label>
                                            <input
                                                type="text"
                                                className="input-box-address"
                                                placeholder="State"
                                                value={formData.state}
                                                required
                                                onChange={(e) =>
                                                    setFormData({ ...formData, state: e.target.value })
                                                }
                                            />
                                            {formErrors.state && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.state[0]}</div>}
                                        </div>
                                        <div className="input-field">
                                            <label>City  <span className="sup-color">*</span></label>
                                            <input
                                                type="text"
                                                className="input-box-address"
                                                placeholder="City"
                                                value={formData.city}
                                                required
                                                onChange={(e) =>
                                                    setFormData({ ...formData, city: e.target.value })
                                                }
                                            />
                                            {formErrors.city && <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>{formErrors.city[0]}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                                <button type="submit" className="am-add-btn">
                                    {editId ? "Update Address" : "Save Address"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}
